import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import { usePatterns, useAcknowledgePattern, useResolvePattern } from '../../hooks/useIntelligence';
import { PatternCard } from '../../components/intelligence';
import { Card } from '../../components/ui/Card';
import { Loading } from '../../components/ui/Loading';
import { EmptyState } from '../../components/ui/EmptyState';
import toast from 'react-hot-toast';

export default function Patterns() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const { data, isLoading, error } = usePatterns(page, 20);
    const acknowledgeMutation = useAcknowledgePattern();
    const resolveMutation = useResolvePattern();

    const handleAcknowledge = (patternId: number) => {
        acknowledgeMutation.mutate(patternId, {
            onSuccess: () => {
                toast.success('Pattern acknowledged');
            },
            onError: () => {
                toast.error('Failed to acknowledge pattern');
            }
        });
    };

    const handleResolve = (patternId: number) => {
        const note = prompt('Add a resolution note (optional):');
        resolveMutation.mutate({ patternId, note: note || undefined }, {
            onSuccess: () => {
                toast.success('Pattern marked as resolved');
            },
            onError: () => {
                toast.error('Failed to resolve pattern');
            }
        });
    };

    if (isLoading) return <Loading message="Loading patterns..." />;

    if (error) {
        return (
            <EmptyState
                icon={AlertCircle}
                title="Error Loading Patterns"
                description="There was an error loading detected patterns. Please try again."
            />
        );
    }

    const patterns = data?.data || [];
    const meta = data?.meta;

    // Calculate summary stats
    const activePatterns = patterns.filter(p => !p.status.is_resolved).length;
    const unacknowledgedPatterns = patterns.filter(p => !p.status.is_acknowledged && !p.status.is_resolved).length;
    const totalPreventableLoss = patterns
        .filter(p => !p.status.is_resolved)
        .reduce((sum, p) => sum + p.impact.estimated_monthly_loss, 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Brain className="text-primary-600" />
                    Detected Patterns
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    AI-detected payment patterns that may indicate systematic issues
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Active Patterns</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">{activePatterns}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <TrendingUp className="text-blue-600" size={24} />
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Unacknowledged</p>
                            <p className="text-2xl font-bold text-orange-600 mt-1">{unacknowledgedPatterns}</p>
                        </div>
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <AlertCircle className="text-orange-600" size={24} />
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Preventable Loss/Month</p>
                            <p className="text-2xl font-bold text-red-600 mt-1">
                                ${totalPreventableLoss.toLocaleString()}
                            </p>
                        </div>
                        <div className="p-3 bg-red-100 rounded-lg">
                            <CheckCircle2 className="text-red-600" size={24} />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Pattern List */}
            {patterns.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={Brain}
                        title="No Patterns Detected"
                        description="Our AI hasn't detected any systematic patterns in your payment data yet. This is good news!"
                    />
                </Card>
            ) : (
                <div className="space-y-4">
                    {patterns.map((pattern) => (
                        <PatternCard
                            key={pattern.id}
                            pattern={pattern}
                            onAcknowledge={() => handleAcknowledge(pattern.id)}
                            onResolve={() => handleResolve(pattern.id)}
                            onClick={() => navigate(`/intelligence/patterns/${pattern.id}`)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {meta && meta.last_page > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-600">
                        Page {meta.current_page} of {meta.last_page}
                    </span>
                    <button
                        onClick={() => setPage(Math.min(meta.last_page, page + 1))}
                        disabled={page === meta.last_page}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
